import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Replace 'G-XXXXXXXXXX' with your actual Google Analytics 4 Measurement ID
// You can set VITE_GA_MEASUREMENT_ID in your .env file or it will use the fallback
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-43YL4MHP1K';

// Check if GA is enabled and configured
const isGAEnabled = () => {
  return typeof window !== 'undefined' && 
         GA_MEASUREMENT_ID && 
         GA_MEASUREMENT_ID.startsWith('G-') && 
         window.gtag;
};

// Initialize Google Analytics
export const initGA = () => {
  // Only initialize if GA_MEASUREMENT_ID is set and valid
  if (!GA_MEASUREMENT_ID || !GA_MEASUREMENT_ID.startsWith('G-')) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Google Analytics Measurement ID not configured. Please set VITE_GA_MEASUREMENT_ID in your .env file');
    }
    return false;
  }

  // Prevent double initialization
  if (window.gtag) {
    return true;
  }

  try {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onerror = () => {
      console.error('Failed to load Google Analytics script');
    };
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(...args) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;
    
    gtag('js', new Date());
    
    // Enhanced configuration with privacy-friendly settings
    gtag('config', GA_MEASUREMENT_ID, {
      page_path: window.location.pathname,
      page_title: document.title,
      page_location: window.location.href,
      send_page_view: true,
      anonymize_ip: true, // Privacy-friendly
      cookie_flags: 'SameSite=None;Secure',
    });

    return true;
  } catch (error) {
    console.error('Error initializing Google Analytics:', error);
    return false;
  }
};

// Track page view with enhanced data
export const trackPageView = (path, title) => {
  if (!isGAEnabled()) return;

  try {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path || window.location.pathname,
      page_title: title || document.title,
      page_location: window.location.href,
    });
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGAEnabled()) return;

  try {
    window.gtag('event', eventName, {
      ...eventParams,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

// Track button clicks
export const trackButtonClick = (buttonName, location = '') => {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: location || window.location.pathname,
    event_category: 'engagement',
    event_label: buttonName,
  });
};

// Track external link clicks
export const trackExternalLink = (url, linkText = '') => {
  trackEvent('external_link_click', {
    link_url: url,
    link_text: linkText,
    event_category: 'outbound',
    event_label: url,
  });
};

// Track social media clicks
export const trackSocialClick = (platform, url) => {
  trackEvent('social_click', {
    social_platform: platform,
    social_url: url,
    event_category: 'social',
    event_label: platform,
  });
};

// Track project views
export const trackProjectView = (projectName, projectId = '') => {
  trackEvent('project_view', {
    project_name: projectName,
    project_id: projectId,
    event_category: 'portfolio',
    event_label: projectName,
  });
};

// Track form submissions
export const trackFormSubmit = (formName, formLocation = '') => {
  trackEvent('form_submit', {
    form_name: formName,
    form_location: formLocation || window.location.pathname,
    event_category: 'engagement',
    event_label: formName,
  });
};

// Track downloads
export const trackDownload = (fileName, fileType = '') => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    event_category: 'download',
    event_label: fileName,
  });
};

// Track search queries
export const trackSearch = (searchTerm, resultsCount = 0) => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
    event_category: 'search',
    event_label: searchTerm,
  });
};

// Track scroll depth
export const trackScrollDepth = (depth) => {
  trackEvent('scroll', {
    scroll_depth: depth,
    event_category: 'engagement',
    event_label: `${depth}%`,
  });
};

// Track time on page
export const trackTimeOnPage = (seconds) => {
  trackEvent('time_on_page', {
    time_seconds: seconds,
    event_category: 'engagement',
    event_label: `${Math.round(seconds)}s`,
  });
};

// Track video plays (if you add videos later)
export const trackVideoPlay = (videoTitle, videoDuration = 0) => {
  trackEvent('video_play', {
    video_title: videoTitle,
    video_duration: videoDuration,
    event_category: 'video',
    event_label: videoTitle,
  });
};

// Set user properties
export const setUserProperty = (propertyName, value) => {
  if (!isGAEnabled()) return;

  try {
    window.gtag('set', 'user_properties', {
      [propertyName]: value,
    });
  } catch (error) {
    console.error('Error setting user property:', error);
  }
};

// Main GoogleAnalytics Component
const GoogleAnalytics = () => {
  const location = useLocation();

  // Initialize GA on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const initialized = initGA();
      
      if (initialized) {
        // Track initial page view
        trackPageView(window.location.pathname, document.title);
      }
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (isGAEnabled()) {
      // Small delay to ensure page title is updated
      setTimeout(() => {
        trackPageView(location.pathname + location.search, document.title);
      }, 100);
    }
  }, [location]);

  // Automatic external link tracking
  useEffect(() => {
    if (typeof window === 'undefined' || !isGAEnabled()) return;

    const handleLinkClick = (e) => {
      const link = e.target.closest('a');
      if (!link) return;

      const href = link.getAttribute('href');
      if (!href) return;

      // Check if it's an external link
      const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
      const isSocial = href.includes('github.com') || 
                      href.includes('linkedin.com') || 
                      href.includes('twitter.com') ||
                      href.includes('x.com');

      if (isExternal) {
        const linkText = link.textContent?.trim() || link.getAttribute('aria-label') || 'External Link';
        
        if (isSocial) {
          let platform = 'unknown';
          if (href.includes('github.com')) platform = 'GitHub';
          else if (href.includes('linkedin.com')) platform = 'LinkedIn';
          else if (href.includes('twitter.com') || href.includes('x.com')) platform = 'Twitter';
          
          trackSocialClick(platform, href);
        } else {
          trackExternalLink(href, linkText);
        }
      }

      // Track download links
      if (href.match(/\.(pdf|doc|docx|xls|xlsx|zip|rar|tar|gz)$/i)) {
        const fileName = href.split('/').pop();
        const fileType = fileName.split('.').pop();
        trackDownload(fileName, fileType);
      }
    };

    document.addEventListener('click', handleLinkClick, true);
    
    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, []);

  // Scroll depth tracking
  useEffect(() => {
    if (typeof window === 'undefined' || !isGAEnabled()) return;

    let maxScroll = 0;
    const milestones = [25, 50, 75, 90, 100];
    const trackedMilestones = new Set();

    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercent = Math.round((scrollTop / (documentHeight - windowHeight)) * 100);

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;

        milestones.forEach((milestone) => {
          if (scrollPercent >= milestone && !trackedMilestones.has(milestone)) {
            trackedMilestones.add(milestone);
            trackScrollDepth(milestone);
          }
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Time on page tracking
  useEffect(() => {
    if (typeof window === 'undefined' || !isGAEnabled()) return;

    const startTime = Date.now();
    let timeTracked = false;

    const trackTime = () => {
      if (timeTracked) return;
      
      const timeSpent = (Date.now() - startTime) / 1000; // in seconds
      
      // Track at 30 seconds, 1 minute, 2 minutes, 5 minutes
      if (timeSpent >= 30 && timeSpent < 60 && !timeTracked) {
        trackTimeOnPage(timeSpent);
        timeTracked = true;
      } else if (timeSpent >= 60) {
        trackTimeOnPage(timeSpent);
        timeTracked = true;
      }
    };

    // Track time when user leaves the page
    const handleBeforeUnload = () => {
      const timeSpent = (Date.now() - startTime) / 1000;
      if (timeSpent > 5) { // Only track if user spent more than 5 seconds
        trackTimeOnPage(timeSpent);
      }
    };

    // Check periodically
    const interval = setInterval(trackTime, 10000); // Check every 10 seconds
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return null;
};

export default GoogleAnalytics;
